import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Table from "../../../core/common/dataTable/index";
import { Button, Drawer, message, Modal, Tag, Empty, Spin } from "antd";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { useDispatch, useSelector } from "react-redux";
import { getEmployees } from "../../../core/data/redux/employee/employeeAction";
import { AppDispatch } from "../../../core/data/redux/store";
import { fetchPayscales } from "../../../core/data/redux/payroll/payrollComponent/payscale/payscaleActions";
import { fetchEarnings } from "../../../core/data/redux/payroll/payrollComponent/earning/earningActions";
import { fetchDeductions } from "../../../core/data/redux/payroll/payrollComponent/deduction/deductionActions";
import Select from "react-select";
import { createEmployeeSalaryStructure, deleteEmployeeSalaryStructure, fetchEmployeeSalaryStructures, fetchSalaryStructureLogsByEmployeeId, updateEmployeeSalaryStructure } from "../../../core/data/redux/payroll/payrollStructure/payrollStructure/employeeSalaryStructureActions";
import { today } from "../../../core/utils/FormatData";
import { formatDate as FormatFeildDate } from "../../../core/common/GlobalFunctions/DateFormats";
import DeleteModal1 from "../../../core/modals/deleteModal1";
import { filterData } from "../../../core/common/GlobalFunctions/tableFilters";
import { exportToCSV, exportToExcel, exportToPDF } from "../../../core/common/GlobalFunctions/exportTableData";
import { status } from "../../../core/common/selectoption/selectoption";
import { fetchOvertimePays } from "../../../core/data/redux/payroll/payrollComponent/overtimePayRate/overtimePayActions";
import { useEmployeeSalaryCSVImport } from "./useEmployeeSalaryCSVImport";
import { formatCurrency } from "../../../core/utils/configFormatters";
import { useAppConfig } from "../../../customhooks/useAppConfig";
import { formatDate as dateFormat } from "../../../core/utils/configFormatters";
import { filterByStatus } from "../../../core/utils/filterActiveData";
import { handleDownloadSample } from "./SalaryStructureSample";
import { decryptData } from "../../../core/utils/encryptUtils";

interface EarningDeductionItem {
    name: string;
    calculateUpon?: string; // Optional as it might not be present initially
    percent?: string;      // Assuming percent is stored as string
    id: string;            // ID from the master list (EarningID/DeductionID)
    amount: number;
    earning_id?: string;   // This seems to be the primary key from backend table `employee_salary_earnings`
    deduction_id?: string; // This seems to be the primary key from backend table `employee_salary_deductions`
}

const EmployeeSalaryStructure = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [selectedStatus, setSelectedStatus] = useState('active');
    const [selectedSort, setSelectedSort] = useState<any>("");
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<any>(null);
    const [tableKey, setTableKey] = useState(0);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const { user } = useSelector((state: any) => state.auth);
    const { overtimePays } = useSelector((state: any) => state.payroll.overtimRate);
    const [selectedRowData, setSelectedRowData] = useState<any>(null);
    const [modalType, setModalType] = useState<'edit' | 'create' | ''>('');
    const { employees } = useSelector((state: any) => state.employee);
    const { earnings: masterEarnings } = useSelector((state: any) => state.payroll.earning); // Renamed for clarity
    const { deductions: masterDeductions } = useSelector((state: any) => state.payroll.deduction); // Renamed for clarity
    const { salaryStructures, loading, employeeSalaryStructureLogs = [] } = useSelector((state: any) => state.payroll.salaryStructure);

    const employeeData = employees ? employees : [];
    const { payscales } = useSelector((state: any) => state.payroll.payrollScall);
    const [basicAmount, setBasicAmount] = useState(0); // Stores the current basic salary value
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { handleCSVImport } = useEmployeeSalaryCSVImport(setTableKey, user);

    const initialEarningDeductionItem: EarningDeductionItem = { name: '', calculateUpon: '', percent: '', id: '', amount: 0, earning_id: '' };
    const [earningsData, setEarningsData] = useState<EarningDeductionItem[]>([initialEarningDeductionItem]);
    const [deductionsData, setDeductionsData] = useState<EarningDeductionItem[]>([initialEarningDeductionItem]);
    const activeEmpl = filterByStatus(employeeData, 'active', "employee_status");
    const [isSampleModalVisible, setIsSampleModalVisible] = useState(false);
    const [isDrawerVisible, setIsDrawerVisible] = useState(false);

    const showSampleModal = () => setIsSampleModalVisible(true);
    const closeSampleModal = () => setIsSampleModalVisible(false);

    const encryptedPermissions = localStorage.getItem('currentPermissions');
    const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];


    const canAdd =
        permissions.includes("Add") || permissions.includes("AllowAll");
    const canView =
        permissions.includes("View") || permissions.includes("AllowAll");
    const canEdit =
        permissions.includes("Edit") || permissions.includes("AllowAll");
    const canDelete =
        permissions.includes("Delete") || permissions.includes("AllowAll");
    const canExport =
        permissions.includes("Export") || permissions.includes("AllowAll");
    const canImport =
        permissions.includes("Import") || permissions.includes("AllowAll");

    const [employeeSalaryData, setEmployeeSalaryData] = useState({
        employee_id: '',
        pay_scale_id: '',
        net_salary: 0,
        start_date: '',
        end_date: '',
        overtime_pay_id: '',
        status: 'active'
    });

    const [errors, setErrors] = useState({
        employee_id: '',
        pay_scale_id: '',
        net_salary: '',
        start_date: '',
        end_date: '',
        overtime_pay_id: ''
    });

    // Get minimum date (employee's joining date) for date inputs
    const selectedEmployeeJoiningDate = useMemo(() => {
        if (!employeeSalaryData.employee_id) return null;
        const selectedEmployee = activeEmpl.find((emp: any) => emp.EmployeeID === employeeSalaryData.employee_id);
        if (selectedEmployee?.DateOfJoining) {
            const date = new Date(selectedEmployee.DateOfJoining);
            return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD for input min attribute
        }
        return null;
    }, [employeeSalaryData.employee_id, activeEmpl]);

    const resetForm = () => {
        setEmployeeSalaryData({
            employee_id: '',
            pay_scale_id: '',
            net_salary: 0,
            start_date: '',
            end_date: '',
            overtime_pay_id: '',
            status: 'active'
        });
        setEarningsData([initialEarningDeductionItem]);
        setDeductionsData([initialEarningDeductionItem]);
        setBasicAmount(0);
        setErrors({ employee_id: '', pay_scale_id: '', net_salary: '', start_date: '', end_date: '', overtime_pay_id: '' });
    };
    const [isPayscaleAutoFilled, setIsPayscaleAutoFilled] = useState(false);

    useEffect(() => {
        if (employeeSalaryData.employee_id && payscales?.length > 0) {
            const selectedEmp = employeeData.find(
                (emp: any) => emp.EmployeeID === employeeSalaryData.employee_id
            );

            const empExperience = selectedEmp?.experience;
            const empRoleId = selectedEmp?.role_id;

            if (empExperience !== null && empExperience !== undefined && empRoleId !== null && empRoleId !== undefined) {
                const matchedPayscale = payscales.find(
                    (ps: any) =>
                        ps.ExperienceLevel === empExperience.toString() &&
                        ps.RoleId === empRoleId
                );

                if (matchedPayscale) {
                    handleInputChange({ target: { value: matchedPayscale.Id } }, 'pay_scale_id');
                    setIsPayscaleAutoFilled(true);
                } else {
                    setIsPayscaleAutoFilled(false);
                }
            } else {
                setIsPayscaleAutoFilled(false);
            }
        }
    }, [employeeSalaryData.employee_id, payscales, employeeData]);



    const triggerImportFileSelect = () => { if (fileInputRef.current) { fileInputRef.current.click(); } };
    const handleImportFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            message.warning("Make sure all the IDs in your CSV match the existing master data correctly.");
            handleCSVImport(file);
        }
        if (fileInputRef.current) { fileInputRef.current.value = ''; }
    };
    const showModal = (type: 'edit' | 'create') => { setModalType(type); setIsModalVisible(true); };
    const handleCancel = () => { setIsModalVisible(false); setModalType(''); resetForm(); };

    const getRecalculatedComponents = (
        currentBasic: number,
        currentEarnings: EarningDeductionItem[],
        currentDeductions: EarningDeductionItem[]
    ) => {
        const recalculatedEarnings = currentEarnings.map(earning => {
            if (earning.name !== 'Basic' && earning.percent && parseFloat(earning.percent) > 0) {
                return {
                    ...earning,
                    amount: parseFloat(((currentBasic * parseFloat(earning.percent)) / 100).toFixed(2))
                };
            }
            return earning;
        });

        const recalculatedDeductions = currentDeductions.map(deduction => {
            if (deduction.percent && parseFloat(deduction.percent) > 0) {
                return {
                    ...deduction,
                    amount: parseFloat(((currentBasic * parseFloat(deduction.percent)) / 100).toFixed(2))
                };
            }
            return deduction;
        });
        return { finalEarnings: recalculatedEarnings, finalDeductions: recalculatedDeductions };
    };

    const handleRemoveEarning = (index: number) => setEarningsData(earningsData.filter((_, i) => i !== index));
    const handleAddEarning = () => setEarningsData([...earningsData, { ...initialEarningDeductionItem }]);
    const handleAddDeduction = () => setDeductionsData([...deductionsData, { ...initialEarningDeductionItem }]);
    const handleRemoveDeduction = (index: number) => setDeductionsData(deductionsData.filter((_, i) => i !== index));

    // add second param with default
    const handleEditSalaryStructureData = (record: any, refreshFromMaster: boolean = false): void => {
        setSelectedRowData(record);

        const startDate = record.start_date ? FormatFeildDate(record.start_date).slice(0, 10) : '';
        const endDate = record.end_date ? FormatFeildDate(record.end_date).slice(0, 10) : '';

        // Find existing Basic from the structure
        const existingBasic = Number(
            record.earnings?.find((e: any) => e.earning_name === 'Basic')?.amount ?? 0
        );

        // If refreshing, try to take Basic from master if present, else fall back
        const masterBasic = masterEarnings?.find((m: any) => m.Name === 'Basic');
        const basicFromMaster = masterBasic ? Number(masterBasic.Amount ?? existingBasic) : existingBasic;
        const nextBasic = refreshFromMaster ? basicFromMaster : existingBasic;

        // Helper to compute earning amount when refreshing
        const computeEarningAmount = (structItem: any) => {
            const m = masterEarnings?.find((me: any) => me.Id === Number(structItem.earning_id));
            if (!refreshFromMaster || !m) return Number(structItem.amount);

            // Update by master: prefer Percent when available; otherwise Amount; otherwise keep
            const percent = m.Percent != null && m.Percent !== '' ? Number(m.Percent) : undefined;

            if (m.Name === 'Basic') {
                return Number(m.Amount ?? structItem.amount ?? nextBasic);
            }
            if (percent && !Number.isNaN(percent) && percent > 0) {
                return Number(((nextBasic * percent) / 100).toFixed(2));
            }
            if (m.Amount != null && m.Amount !== '') {
                return Number(m.Amount);
            }
            return Number(structItem.amount);
        };

        // Helper to compute deduction amount when refreshing
        const computeDeductionAmount = (structItem: any) => {
            const m = masterDeductions?.find((md: any) => md.Id === Number(structItem.deduction_id));
            if (!refreshFromMaster || !m) return Number(structItem.amount);

            const percent = m.Percent != null && m.Percent !== '' ? Number(m.Percent) : undefined;

            if (percent && !Number.isNaN(percent) && percent > 0) {
                return Number(((nextBasic * percent) / 100).toFixed(2));
            }
            if (m.Amount != null && m.Amount !== '') {
                return Number(m.Amount);
            }
            return Number(structItem.amount);
        };

        // Earnings
        let initialBasic = 0;
        const earningsFormatted = (record.earnings || []).map((earning: any) => {
            const master = masterEarnings?.find((me: any) => me.Id === Number(earning.earning_id));

            // name & percent possibly updated from master if refreshing
            const name = refreshFromMaster && master ? master.Name : earning.earning_name;
            const percent =
                refreshFromMaster && master && master.Percent != null && master.Percent !== ''
                    ? String(master.Percent)
                    : earning.percent
                        ? String(earning.percent)
                        : '';

            const amount = computeEarningAmount(earning);

            if (name === 'Basic') initialBasic = amount;

            return {
                name,
                amount: Number(amount),
                id: String(earning.earning_id),
                earning_id: String(earning.id),
                percent,
                calculateUpon:
                    master?.CalculatedUpon ??
                    masterEarnings.find((me: any) => me.Id === Number(earning.earning_id))?.CalculatedUpon ??
                    '',
            };
        });

        // If Basic wasn’t present, fall back to nextBasic
        if (!initialBasic) initialBasic = nextBasic;

        // Deductions
        const deductionsFormatted = (record.deductions || []).map((deduction: any) => {
            const master = masterDeductions?.find((md: any) => md.Id === Number(deduction.deduction_id));

            const name = refreshFromMaster && master ? master.Name : deduction.deduction_name;
            const percent =
                refreshFromMaster && master && master.Percent != null && master.Percent !== ''
                    ? String(master.Percent)
                    : deduction.percent
                        ? String(deduction.percent)
                        : '';

            const amount = computeDeductionAmount(deduction);

            return {
                name,
                amount: Number(amount),
                id: String(deduction.deduction_id),
                deduction_id: String(deduction.id),
                percent,
                calculateUpon:
                    master?.CalculatedUpon ??
                    masterDeductions.find((md: any) => md.Id === Number(deduction.deduction_id))?.CalculatedUpon ??
                    '',
            };
        });

        setEmployeeSalaryData({
            employee_id: record.employee_id,
            pay_scale_id: record.pay_scale_id,
            net_salary: record.net_salary, // will recalc on render anyway
            status: record.status,
            start_date: startDate,
            end_date: endDate,
            overtime_pay_id: record.overtime_pay_id,
        });

        setBasicAmount(initialBasic);
        setEarningsData(earningsFormatted.length > 0 ? earningsFormatted : [initialEarningDeductionItem]);
        setDeductionsData(deductionsFormatted.length > 0 ? deductionsFormatted : [initialEarningDeductionItem]);

        showModal('edit');
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const calculateNetSalary = useCallback(() => {
        const totalEarnings = earningsData.reduce((acc, earning) => acc + (earning.amount || 0), 0);
        const totalDeductions = deductionsData.reduce((acc, deduction) => acc + (deduction.amount || 0), 0);
        const net_salary = totalEarnings - totalDeductions;

        setEmployeeSalaryData((prevState) => ({
            ...prevState,
            net_salary: parseFloat(net_salary.toFixed(2)),
        }));
    }, [earningsData, deductionsData]);

    useEffect(() => {
        calculateNetSalary();
    }, [calculateNetSalary]);

    const handleInputChange = (e: any, field: string) => {
        const { value } = e.target;
        setEmployeeSalaryData((prevState) => ({ ...prevState, [field]: value, }));
        setErrors((prevErrors: any) => ({ ...prevErrors, [field]: '', }));
    };

    const handleEarningSelect = (index: number, earningIdStr: string) => {
        const earningId = parseInt(earningIdStr);
        const selectedMasterEarning = masterEarnings.find((earning: any) => earning.Id === earningId);

        if (selectedMasterEarning) {
            const mutableEarnings = [...earningsData];
            const percentStr = selectedMasterEarning.Percent ? selectedMasterEarning.Percent.toString() : '';
            const percentNum = parseFloat(percentStr);

            let calculatedAmount = parseFloat(selectedMasterEarning.Amount || 0);

            if (selectedMasterEarning.Name === 'Basic') {
            } else if (basicAmount > 0 && percentStr && percentNum > 0) {
                calculatedAmount = parseFloat(((basicAmount * percentNum) / 100).toFixed(2));
            }

            mutableEarnings[index] = {
                name: selectedMasterEarning.Name,
                calculateUpon: selectedMasterEarning.CalculatedUpon,
                percent: percentStr,
                id: selectedMasterEarning.Id.toString(),
                amount: calculatedAmount,
                earning_id: earningsData[index].earning_id || '',
            };

            if (selectedMasterEarning.Name === 'Basic') {
                setBasicAmount(calculatedAmount);
                const { finalEarnings, finalDeductions } = getRecalculatedComponents(
                    calculatedAmount,
                    mutableEarnings,
                    deductionsData
                );
                setEarningsData(finalEarnings);
                setDeductionsData(finalDeductions);
            } else {
                setEarningsData(mutableEarnings);
            }
        }
    };

    const handleDeductionSelect = (index: number, deductionIdStr: string) => {
        const deductionId = parseInt(deductionIdStr);
        const selectedMasterDeduction = masterDeductions.find((deduction: any) => deduction.Id === deductionId);

        if (selectedMasterDeduction) {
            const mutableDeductions = [...deductionsData];
            const percentStr = selectedMasterDeduction.Percent ? selectedMasterDeduction.Percent.toString() : '';
            const percentNum = parseFloat(percentStr);

            let calculatedAmount = parseFloat(selectedMasterDeduction.Amount || 0); // Default to master amount or 0

            if (basicAmount > 0 && percentStr && percentNum > 0) {
                calculatedAmount = parseFloat(((basicAmount * percentNum) / 100).toFixed(2));
            }

            mutableDeductions[index] = {
                name: selectedMasterDeduction.Name,
                calculateUpon: selectedMasterDeduction.CalculatedUpon,
                id: selectedMasterDeduction.Id.toString(),
                percent: percentStr,
                amount: calculatedAmount,
                deduction_id: deductionsData[index].deduction_id || '', // Preserve existing PK
            };
            setDeductionsData(mutableDeductions);
        }
    };

    const handleAmountChange = (index: number, value: number, type: 'earning' | 'deduction') => {
        if (type === 'earning') {
            const tempUpdatedEarnings = [...earningsData];
            tempUpdatedEarnings[index].amount = isNaN(value) ? 0 : value; // Ensure value is a number

            if (tempUpdatedEarnings[index].name === 'Basic') {
                const newBasic = isNaN(value) ? 0 : value;
                setBasicAmount(newBasic); // Update the global basic amount state

                const { finalEarnings, finalDeductions } = getRecalculatedComponents(
                    newBasic,               // Use the new user-inputted basic amount
                    tempUpdatedEarnings,    // Pass the array that includes the just-modified Basic amount
                    deductionsData
                );
                setEarningsData(finalEarnings);
                setDeductionsData(finalDeductions);
            } else {
                // If a non-Basic earning amount is changed, just update that earning
                setEarningsData(tempUpdatedEarnings);
            }
        } else if (type === 'deduction') {
            const updatedDeductions = [...deductionsData];
            updatedDeductions[index].amount = isNaN(value) ? 0 : value; // Ensure value is a number
            setDeductionsData(updatedDeductions);
        }
    };


    useEffect(() => {
        // Initial data fetching
        if (!employees?.length) dispatch(getEmployees());
        if (!payscales?.length) dispatch(fetchPayscales());
        if (!masterEarnings?.length) dispatch(fetchEarnings());
        if (!masterDeductions.length) dispatch(fetchDeductions());
        if (!salaryStructures?.length) dispatch(fetchEmployeeSalaryStructures());
        if (overtimePays?.length === 0) dispatch(fetchOvertimePays());
    }, [dispatch]);

    // ---------------------------------------------------
    // AI → CREATE SALARY STRUCTURE (SCREEN)
    // ---------------------------------------------------
    useEffect(() => {
        const handler = (event: any) => {
            const data = event.detail;

            if (!data?.employee_id || !data?.pay_scale_id) {
                message.error("Invalid AI salary structure data");
                return;
            }

            setModalType("create");
            setIsModalVisible(true);

            setEmployeeSalaryData({
                employee_id: data.employee_id,
                pay_scale_id: data.pay_scale_id,
                start_date: data.start_date || "",
                end_date: data.end_date || "",
                overtime_pay_id: data.overtime_pay_id || "",
                net_salary: 0,
                status: "active"
            });

            message.success("Salary structure form prepared by AI");
        };

        window.addEventListener("ai-create-salary-structure", handler);

        return () => {
            window.removeEventListener("ai-create-salary-structure", handler);
        };
    }, []);


    const validateForm = () => {
        // Validation logic remains the same
        let isValid = true;
        const newErrors: any = {};
        if (!employeeSalaryData.employee_id) { newErrors.employee_id = 'Please select an employee'; isValid = false; }
        if (!employeeSalaryData.pay_scale_id) { newErrors.pay_scale_id = 'Please select a payscale'; isValid = false; }
        // if (!employeeSalaryData.overtime_pay_id) { newErrors.overtime_pay_id = 'Please select a overtime Pay'; isValid = false; }
        if (employeeSalaryData.net_salary <= 0) { newErrors.net_salary = 'Net Salary must be greater than 0'; isValid = false; }
        
        // Get selected employee's DateOfJoining for validation
        const selectedEmployee = activeEmpl.find((emp: any) => emp.EmployeeID === employeeSalaryData.employee_id);
        const joiningDate = selectedEmployee?.DateOfJoining ? new Date(selectedEmployee.DateOfJoining) : null;
        
        if (!employeeSalaryData.start_date) { 
            newErrors.start_date = 'Please select a start date'; 
            isValid = false; 
        } else if (joiningDate && new Date(employeeSalaryData.start_date) < joiningDate) {
            newErrors.start_date = `Start date cannot be before employee's joining date (${joiningDate.toLocaleDateString()})`;
            isValid = false;
        }
        
        if (!employeeSalaryData.end_date) { 
            newErrors.end_date = 'Please select an end date'; 
            isValid = false; 
        } else if (joiningDate && new Date(employeeSalaryData.end_date) < joiningDate) {
            newErrors.end_date = `End date cannot be before employee's joining date (${joiningDate.toLocaleDateString()})`;
            isValid = false;
        } else if (employeeSalaryData.start_date && new Date(employeeSalaryData.end_date) <= new Date(employeeSalaryData.start_date)) { 
            newErrors.end_date = 'End date must be after start date'; 
            isValid = false; 
        }
        
        setErrors(newErrors);
        return isValid;
    };
    const onSalaryStructureSuccess = async (res: any) => {
        message.success(res?.message || res?.Message || 'Employee Salary Structure saved successfully');
        await dispatch(fetchEmployeeSalaryStructures());
        setTableKey((prev: number) => prev + 1);
        resetForm();
        handleCancel();
    };

    const confirmSalaryEdit = (onOk: () => Promise<void>, startDate?: string) =>
        Modal.confirm({
            title: 'Confirm salary structure update',
            content:
                `You’re about to update this employee’s salary structure.\n\n` +
                `This will NOT affect any previously processed months.\n` +
                `The change will apply to future payrolls starting from ${startDate || 'the selected start date'}.\n\n` +
                `Proceed?`,
            okText: 'Update salary structure',
            cancelText: 'Cancel',
            centered: true,
            onOk,
        });
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        const updatedFormData = {
            ...employeeSalaryData,
            overtime_pay_id: employeeSalaryData.overtime_pay_id || null,
            CreatedBy: modalType === 'create' ? user.UserId : undefined,
            ModifiedBy: modalType === 'edit' ? user.UserId : undefined,
            earnings: earningsData
                .filter((e) => e.id && e.name)
                .map((earning) => ({
                    earning_name: earning.name,
                    amount: earning.amount,
                    id: earning.earning_id,
                    earning_id: earning.id,
                    percent:
                        earning.percent !== '' && earning.percent !== undefined
                            ? Number(earning.percent)
                            : null,
                    CreatedBy: user.UserId,
                })),
            deductions: deductionsData
                .filter((d) => d.id && d.name)
                .map((deduction) => ({
                    deduction_name: deduction.name,
                    amount: deduction.amount,
                    id: deduction.deduction_id,
                    deduction_id: deduction.id,
                    percent:
                        deduction.percent !== '' && deduction.percent !== undefined
                            ? Number(deduction.percent)
                            : null,
                    CreatedBy: user.UserId,
                })),
        };

        try {
            if (modalType === 'create') {
                const res = await dispatch(createEmployeeSalaryStructure(updatedFormData)).unwrap();
                await onSalaryStructureSuccess(res);
                return;
            }

            if (modalType === 'edit' && selectedRowData) {
                return confirmSalaryEdit(
                    async () => {
                        const res = await dispatch(
                            updateEmployeeSalaryStructure({
                                id: selectedRowData.salary_structure_id,
                                ...updatedFormData,
                            })
                        ).unwrap();
                        await onSalaryStructureSuccess(res);
                    },
                    employeeSalaryData.start_date
                );
            }
        } catch (error: any) {
            const errorMessage = error?.error || error?.message || 'An error occurred during submission.';
            message.error(errorMessage);
        }
    };


    const handleDeleteSalaryStructure = async (id: number) => {
        // ... (delete logic remains same)
        try {
            const result = await dispatch(deleteEmployeeSalaryStructure(id));
            if (result.meta.requestStatus === 'fulfilled') {
                message.success('Salary Structure deleted successfully'); // Changed alert to message
                await dispatch(fetchEmployeeSalaryStructures());
                setTableKey((prevKey: any) => prevKey + 1);
                setIsDeleteModalVisible(false);
            } else {
                message.error(result.payload.error || 'Failed to delete salary structure.'); // Changed alert to message
            }
        } catch (error) {
            message.error('Something went wrong during deletion.'); // Changed alert to message
        }
    };

    const handleCreateSameStructure = () => {
        // ... (logic for create same structure remains same)
        if (!selectedRowData) return;
        const newSalaryData = {
            employee_id: '', // Clear employee ID for new structure
            pay_scale_id: selectedRowData.pay_scale_id,
            net_salary: selectedRowData.net_salary,
            status: 'active', // Default to active for new one
            start_date: '', // Clear dates or set to today
            end_date: '',   // Clear dates
            overtime_pay_id: selectedRowData.overtime_pay_id
        };

        let initialBasic = 0;
        const earningsFormatted = selectedRowData.earnings.map((earning: any) => {
            if (earning.earning_name === 'Basic') {
                initialBasic = parseFloat(earning.amount);
            }
            return {
                name: earning.earning_name,
                amount: parseFloat(earning.amount),
                id: earning.earning_id.toString(),
                earning_id: '', // This will be a new record, so no existing PK
                percent: earning.percent ? earning.percent.toString() : '',
                calculateUpon: masterEarnings.find((me: any) => me.Id === earning.earning_id)?.CalculatedUpon || ''
            };
        });

        const deductionsFormatted = selectedRowData.deductions.map((deduction: any) => ({
            name: deduction.deduction_name,
            amount: parseFloat(deduction.amount),
            id: deduction.deduction_id.toString(),
            deduction_id: '', // New record
            percent: deduction.percent ? deduction.percent.toString() : '',
            calculateUpon: masterDeductions.find((md: any) => md.Id === deduction.deduction_id)?.CalculatedUpon || ''
        }));

        setBasicAmount(initialBasic);
        setEmployeeSalaryData(newSalaryData);
        setEarningsData(earningsFormatted.length > 0 ? earningsFormatted : [initialEarningDeductionItem]);
        setDeductionsData(deductionsFormatted.length > 0 ? deductionsFormatted : [initialEarningDeductionItem]);
        setErrors({ employee_id: '', pay_scale_id: '', net_salary: '', start_date: '', end_date: '', overtime_pay_id: '' });
        setModalType('create'); // Set to create mode
        setIsModalVisible(true);
    };
    const data = salaryStructures;
    const filteredData = useMemo(() => {
        const filters =
            selectedStatus && selectedStatus !== "All"
                ? { status: selectedStatus }
                : {};
        return filterData(data || [], filters, "CreatedAt", "CreatedAt", selectedSort)
    }, [data, selectedSort, selectedStatus]);

    const ExportData = filteredData.map((item: any) => ({
        EmployeeNumber: item.EmployeeNumber || "N/A",
        EmployeeName: item.EmployeeName || "N/A",
        MobileNumber: item.MobileNumber || "N/A",
        net_salary: item.net_salary ?? 0,
        status: item.status || "Inactive",
        start_date: item.start_date ? new Date(item.start_date).toLocaleDateString("en-IN") : "N/A",
        end_date: item.end_date ? new Date(item.end_date).toLocaleDateString("en-IN") : "N/A",
        CreatedAt: item.CreatedAt ? new Date(item.CreatedAt).toLocaleDateString("en-IN") : "N/A",
        JoiningDate: item.JoiningDate ? new Date(item.JoiningDate).toLocaleDateString("en-IN") : "N/A",
        EmployeeDesignation: item.EmployeeDesignation || "N/A",
    }));


    useEffect(() => {
        setTableKey(prevKey => prevKey + 1);
    }, [selectedSort, selectedStatus, salaryStructures]);

    const config = useAppConfig();

    const handleShowLogs = (record: any) => {
        const empId = Number(record?.employee_id);
        if (!empId) return;
        setSelectedEmployeeId(empId);
        dispatch(fetchSalaryStructureLogsByEmployeeId({ employeeId: empId }));
        setIsDrawerVisible(true);
    };
    const cap = (s?: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '-');
    const actionTagColor = (a?: string) =>
        a === 'INSERT' ? 'green' : a === 'UPDATE' ? 'blue' : a === 'DELETE' ? 'red' : 'default';

    const safeDate = (d?: string | null) => (d ? dateFormat(d, config) : '-');
    const safeMoney = (n?: string | number) =>
        formatCurrency(Number(n || 0), config);
    // Put this near other helpers
    const showEditHeadsUp = (record: any) =>
        Modal.confirm({
            title: 'Update component heads from master?',
            content: (
                <>
                    <div>
                        Choose <strong>Use updated masters</strong> to refresh earning/deduction amounts & percentages from the
                        current master settings, or <strong>Keep existing</strong> to edit with the values this structure already has.
                    </div>
                </>
            ),
            okText: 'Use updated masters',
            cancelText: 'Keep existing',
            centered: true,
            onOk: () => handleEditSalaryStructureData(record, true),
            onCancel: () => handleEditSalaryStructureData(record, false),
        });


    const columns = [
        { title: "Emp ID", dataIndex: "EmployeeNumber", sorter: (a: any, b: any) => a.EmployeeNumber.localeCompare(b.EmployeeNumber) },
        {
            title: "Name",
            dataIndex: "EmployeeName",
            sorter: (a: any, b: any) => a.EmployeeName.localeCompare(b.EmployeeName),
            render: (text: String, record: any) => (
                <div
                    className="d-flex align-items-center file-name-icon"
                    style={{ gap: 8 }}
                    onMouseEnter={(e) => e.currentTarget.querySelector("button")?.style.setProperty("opacity", "0.6")}
                    onMouseLeave={(e) => e.currentTarget.querySelector("button")?.style.setProperty("opacity", "0")}
                >
                    <div className="avatar avatar-md">
                        <img
                            src={record.PictureURL || 'https://cdn-icons-png.flaticon.com/512/149/149071.png'}
                            alt="img"
                            className="img-fluid rounded-circle"
                            onError={(e) => (e.currentTarget.src = 'https://cdn-icons-png.flaticon.com/512/149/149071.png')}
                            style={{ width: 32, height: 32 }}
                        />
                    </div>
                    <div className="ms-2">
                        <h6 className="fw-medium mb-0">
                            <Link to="#">{record.EmployeeName}</Link>
                        </h6>
                    </div>
                    <button
                        className="btn btn-link p-0"
                        onClick={(e) => {
                            e.currentTarget.blur();
                            if (canEdit) showEditHeadsUp(record);
                        }}
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        title="Edit"
                        disabled={!canEdit}
                        style={{
                            cursor: canEdit ? "pointer" : "not-allowed",
                            opacity: 0,
                            transition: "opacity 0.3s",
                        }}
                    >
                        <i className="ti ti-pencil" />
                    </button>
                </div>
            )
        },
        { title: "Phone", dataIndex: "MobileNumber", sorter: (a: any, b: any) => a.MobileNumber.localeCompare(b.MobileNumber) },
        { title: "Joining Date", dataIndex: "JoiningDate", render: (text: string) => text ? dateFormat(text, config) : '-', sorter: (a: any, b: any) => new Date(a.JoiningDate).getTime() - new Date(b.JoiningDate).getTime() },
        { title: "Start Date", dataIndex: "start_date", render: (text: string) => text ? dateFormat(text, config) : '-', sorter: (a: any, b: any) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime() },
        { title: "Designation", dataIndex: "EmployeeDesignation", sorter: (a: any, b: any) => a.EmployeeDesignation.localeCompare(b.EmployeeDesignation) },
        { title: "End Date", dataIndex: "end_date", render: (text: string) => text ? dateFormat(text, config) : '-', sorter: (a: any, b: any) => new Date(a.end_date).getTime() - new Date(b.end_date).getTime() },
        { title: "Salary", dataIndex: "net_salary", render: (val: number) => formatCurrency(val || 0, config), sorter: (a: any, b: any) => a.net_salary - b.net_salary },
        {
            title: "Status", dataIndex: "status", render: (text: string) => (<span className={`badge ${text === 'active' ? 'badge-success' : 'badge-danger'} d-inline-flex align-items-center badge-xs`}><i className="ti ti-point-filled me-1" />{text ? text.charAt(0).toUpperCase() + text.slice(1) : ""}
            </span>)
        },
        {
            title: "Action",
            dataIndex: "action",
            render: (_: any, record: any) => {
                const isDeleteDisabled = new Date(record.start_date) < new Date();
                const isViewDisabled = !canView;

                return (
                    <div className="action-icon d-inline-flex">
                        {/* Edit Button */}
                        <button
                            className={`me-2 btn btn-sm btn-outline-primary`}
                            onClick={(e) => {
                                e.currentTarget.blur();
                                if (canEdit) showEditHeadsUp(record);
                            }}
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title="Edit"
                            disabled={!canEdit}
                            style={{ cursor: canEdit ? "pointer" : "not-allowed", opacity: canEdit ? 0.6 : 1, }}
                        >
                            <i className="ti ti-edit" />
                        </button>

                        {/* Delete Button */}
                        <button
                            onClick={(e) => { e.currentTarget.blur(); if (canDelete && !isDeleteDisabled) { setSelectedRowData(record); setIsDeleteModalVisible(true); } }}
                            className={`me-2 btn btn-sm ${!canDelete || isDeleteDisabled ? "btn-outline-secondary disabled-button" : "btn-outline-danger"}`}
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title={!canDelete ? "You do not have permission to delete" : isDeleteDisabled ? "Cannot delete after start date" : "Delete"}
                            disabled={!canDelete || isDeleteDisabled}
                            style={{ cursor: !canDelete || isDeleteDisabled ? "not-allowed" : "pointer", opacity: !canDelete || isDeleteDisabled ? 0.6 : 1, }}
                        >
                            <i className="ti ti-trash" />
                        </button>


                        {/* View Logs Button */}
                        <button
                            onClick={() => {
                                if (!isViewDisabled) handleShowLogs(record);
                            }}
                            className={`btn btn-sm me-2 ${isViewDisabled ? 'btn-outline-secondary disabled-button' : 'btn-outline-dark'}`}
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title={isViewDisabled ? "Permission Denied" : "View Logs"}
                            disabled={isViewDisabled}
                        >
                            <i className="ti ti-clock" />
                        </button>
                    </div>
                );
            }
        }
    ];
    return (
        <>
            {/* Page Wrapper */}
            <div className="page-wrapper">
                <div className="content">
                    {/* Breadcrumb */}
                    <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                        {/* ... (Breadcrumb JSX remains the same) */}
                        <div className="my-auto mb-2">
                            <h2 className="mb-1">Employee Salary Structure</h2>
                            <nav>
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">
                                        <Link to={all_routes.adminDashboard}><i className="ti ti-smart-home" /></Link>
                                    </li>
                                    <li className="breadcrumb-item">HR</li>
                                    <li className="breadcrumb-item active" aria-current="page">Employee Salary Structure</li>
                                </ol>
                            </nav>
                        </div>
                        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
                            {canExport && (
                                <div className="me-2 mb-2">
                                    <div className="dropdown">
                                        <Link to="#" className="dropdown-toggle btn btn-white d-inline-flex align-items-center" data-bs-toggle="dropdown">
                                            <i className="ti ti-file-export me-1" />Export
                                        </Link>
                                        <ul className="dropdown-menu  dropdown-menu-end p-3">
                                            <li><button onClick={() => exportToCSV(ExportData, 'employee_salary_data')} className="dropdown-item rounded-1"><i className="ti ti-file-type-csv me-1" />Export as CSV</button></li>
                                            <li><button onClick={() => exportToExcel(ExportData, 'employee_salary_data')} className="dropdown-item rounded-1"><i className="ti ti-file-type-xls me-1" />Export as Excel</button></li>
                                            <li><button onClick={() => exportToPDF(ExportData, 'employee_salary_data')} className="dropdown-item rounded-1"><i className="ti ti-file-type-xls me-1" />Export as PDF</button></li>
                                        </ul>
                                    </div>
                                </div>
                            )}
                            {canImport && (
                                <div className="me-2 mb-2">
                                    <div className="dropdown">
                                        <Link to="#" className="dropdown-toggle btn btn-white d-inline-flex align-items-center" data-bs-toggle="dropdown">
                                            <i className="ti ti-file-import me-1" />Import
                                        </Link>
                                        <ul className="dropdown-menu dropdown-menu-end p-3">
                                            <li>
                                                <button type="button" onClick={triggerImportFileSelect} className="dropdown-item rounded-1">
                                                    <i className="ti ti-file-spreadsheet me-1" />Import as CSV
                                                </button>
                                            </li>
                                            <li>
                                                <button type="button" onClick={showSampleModal} className="dropdown-item rounded-1">
                                                    <i className="ti ti-download me-1" />View Sample Format
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                    <input type="file" accept=".csv" style={{ display: 'none' }} ref={fileInputRef} onChange={handleImportFileChange} />
                                </div>
                            )}
                            {canAdd && (
                                <div className="mb-2">
                                    <button onClick={() => showModal('create')} className="btn btn-primary d-flex align-items-center">
                                        <i className="ti ti-circle-plus me-2" />Add Salary Structure
                                    </button>
                                </div>
                            )}
                            <div className="head-icons ms-2"><CollapseHeader /></div>
                        </div>
                    </div>
                    {/* /Breadcrumb */}
                    <div className="card">
                        <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                            {/* ... (Card Header JSX remains the same) */}
                            <h5>Employee Salary Structure List</h5>
                            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                                <div className="dropdown me-3">
                                    <Link to="#" className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center" data-bs-toggle="dropdown">
                                        {selectedStatus ? selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1) : "Select Status"}
                                    </Link>
                                    <ul className="dropdown-menu  dropdown-menu-end p-3">
                                        <li>
                                            <Link
                                                to="#"
                                                onClick={() => setSelectedStatus("All")}
                                                className="dropdown-item rounded-1"
                                            >
                                                All
                                            </Link>
                                        </li>
                                        <li><Link to="#" onClick={() => setSelectedStatus('active')} className="dropdown-item rounded-1">Active</Link></li>
                                        <li><Link to="#" onClick={() => setSelectedStatus('inactive')} className="dropdown-item rounded-1">Inactive</Link></li>
                                    </ul>
                                </div>
                                <div className="dropdown">
                                    <Link to="#" className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center" data-bs-toggle="dropdown">
                                        {selectedSort ? `Sort By : ${selectedSort}` : "Sort By : Last 7 Days"}
                                    </Link>
                                    <ul className="dropdown-menu dropdown-menu-end p-3">
                                        <li><Link to="#" onClick={() => setSelectedSort("Recently Added")} className="dropdown-item rounded-1">Recently Added</Link></li>
                                        <li><Link to="#" onClick={() => setSelectedSort("Ascending")} className="dropdown-item rounded-1">Ascending</Link></li>
                                        <li><Link to="#" onClick={() => setSelectedSort("Descending")} className="dropdown-item rounded-1">Descending</Link></li>
                                        <li><Link to="#" onClick={() => setSelectedSort("Last Month")} className="dropdown-item rounded-1">Last Month</Link></li>
                                        <li><Link to="#" onClick={() => setSelectedSort("Last 7 Days")} className="dropdown-item rounded-1">Last 7 Days</Link></li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="card-body p-0">
                            <Table key={tableKey} dataSource={filteredData} columns={columns} /> {/* Removed Selection={true} if not used */}
                        </div>
                    </div>
                </div>
                <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
                    <p className="mb-0">2024 - 2025© hrmtree.</p>
                    <p>Developed By <Link to="#" className="text-primary">Leadchain Systems</Link></p>
                </div>
            </div>
            {/* /Page Wrapper */}
            {/* Add Salary Structure Drawer */}
            <Drawer
                title={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>{modalType === "edit" ? "Edit Employee Salary Structure" : "Add Employee Salary Structure"}</span>
                        {modalType === "edit" && selectedRowData && ( // Ensure selectedRowData exists
                            <Button type="primary" onClick={handleCreateSameStructure}>
                                Create same salary structure
                            </Button>
                        )}
                    </div>
                }
                placement="right"
                width={800}
                onClose={handleCancel}
                open={isModalVisible}
                destroyOnClose
                style={{ marginTop: 50, paddingBottom: 50 }}
            >
                <form onSubmit={handleSubmit}>
                    <div className="modal-body pb-0" style={{ maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' }}> {/* Make modal scrollable */}
                        <div className="row">
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label className="form-label">Employee Name&nbsp; <span className="text-danger"> *</span></label>
                                    <Select
                                        options={[...activeEmpl].sort((a: any, b: any) => a.Name.localeCompare(b.Name)).map((emp: any) => ({ value: emp.EmployeeID, label: emp.Name }))}
                                        value={activeEmpl.find((emp: any) => emp.EmployeeID === employeeSalaryData.employee_id) ? { value: employeeSalaryData.employee_id, label: employeeData.find((emp: any) => emp.EmployeeID === employeeSalaryData.employee_id)?.Name } : null}
                                        onChange={(selectedOption: any) => handleInputChange({ target: { value: selectedOption ? selectedOption.value : '' } }, 'employee_id')}
                                        placeholder="Select Employee"
                                        isClearable
                                    />
                                    {errors.employee_id && <div className="text-danger" style={{ fontSize: '12px' }}>{errors.employee_id}</div>}
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label className="form-label">Payscale&nbsp; <span className="text-danger"> *</span></label>
                                    <select
                                        className="form-select"
                                        value={employeeSalaryData.pay_scale_id}
                                        onChange={(e) => handleInputChange(e, 'pay_scale_id')}
                                    >
                                        <option value="">Select Payscale</option>
                                        {payscales?.map((pay: any) => (
                                            <option key={pay.Id} value={pay.Id}>
                                                {pay.SalaryRangeMin} - {pay.SalaryRangeMax}
                                            </option>
                                        ))}
                                    </select>

                                    {errors.pay_scale_id && <div className="text-danger" style={{ fontSize: '12px' }}>{errors.pay_scale_id}</div>}
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label className="form-label">Overtime Pay</label>
                                    <select className="form-select" value={employeeSalaryData.overtime_pay_id} onChange={(e) => handleInputChange(e, 'overtime_pay_id')}>
                                        <option value="">Select Overtime Pay Rule</option>
                                        {overtimePays?.map((pay: any) => (<option key={pay.ID} value={pay.ID}>{pay.Name}</option>))}
                                    </select>
                                    {errors.overtime_pay_id && <div className="text-danger" style={{ fontSize: '12px' }}>{errors.overtime_pay_id}</div>}
                                </div>
                            </div>
                        </div>
                        <div>
                            <h5 className="text-primary mb-2">Earnings</h5>
                            <table className="table table-bordered table-sm"> {/* table-sm for smaller padding */}
                                <thead><tr><th style={{ width: '5%' }}>#</th><th style={{ width: '40%' }}>Name</th><th style={{ width: '30%' }}>Amount</th><th style={{ width: '25%' }}>Action</th></tr></thead>
                                <tbody>
                                    {earningsData.map((earning, index) => (
                                        <tr key={`earning-${index}`}> {/* Ensure unique key */}
                                            <td>{index + 1}</td>
                                            <td className="position-relative">
                                                <select className="form-select form-select-sm" value={earning.id || ''} onChange={(e) => handleEarningSelect(index, e.target.value)}>
                                                    <option value="">Select Earning</option>
                                                    {masterEarnings?.filter((e: any) => !earningsData.some((ed, idx) => idx !== index && ed.id === e.Id.toString())).map((e: any) => (<option key={e.Id} value={e.Id}>{e.Name}</option>))}
                                                </select>
                                                {earning?.percent && parseFloat(earning.percent) > 0 && (<span className="form-label text-danger position-absolute" style={{ right: '34px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8em' }}>{earning.percent}%</span>)}
                                            </td>
                                            <td><input type="text" className="form-control form-control-sm text-end" value={earning.amount} onChange={(e) => {
                                                const value = e.target.value;
                                                if (/^\d*\.?\d*$/.test(value)) {
                                                    handleAmountChange(index, parseFloat(value) || 0, 'earning');
                                                }
                                            }} /></td>
                                            <td>
                                                {earningsData.length > 1 && (<button type="button" className="btn btn-danger btn-sm" onClick={() => handleRemoveEarning(index)}>-</button>)}
                                                {index === earningsData.length - 1 && (<button type="button" className="btn btn-success btn-sm ms-1" onClick={handleAddEarning}>+</button>)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <h5 className="text-primary mb-2 mt-3">Deductions</h5>
                            <table className="table table-bordered table-sm">
                                <thead><tr><th style={{ width: '5%' }}>#</th><th style={{ width: '40%' }}>Name</th><th style={{ width: '30%' }}>Amount</th><th style={{ width: '25%' }}>Action</th></tr></thead>
                                <tbody>
                                    {deductionsData.map((deduction, index) => (
                                        <tr key={`deduction-${index}`}> {/* Ensure unique key */}
                                            <td>{index + 1}</td>
                                            <td className="position-relative">
                                                <select className="form-select form-select-sm" value={deduction.id || ''} onChange={(e) => handleDeductionSelect(index, e.target.value)}>
                                                    <option value="">Select Deduction</option>
                                                    {masterDeductions?.filter((d: any) => !deductionsData.some((dd, idx) => idx !== index && dd.id === d.Id.toString())).map((d: any) => (<option key={d.Id} value={d.Id}>{d.Name}</option>))}
                                                </select>
                                                {deduction?.percent && parseFloat(deduction.percent) > 0 && (<span className="form-label text-danger position-absolute" style={{ right: '34px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8em' }}>{deduction.percent}%</span>)}
                                            </td>
                                            <td><input type="text" className="form-control form-control-sm text-end" value={deduction.amount} onChange={(e) => {
                                                const value = e.target.value;
                                                if (/^\d*\.?\d*$/.test(value)) {
                                                    handleAmountChange(index, parseFloat(value) || 0, 'deduction');
                                                }
                                            }} /></td>
                                            <td>
                                                {deductionsData.length > 1 && (<button type="button" className="btn btn-danger btn-sm" onClick={() => handleRemoveDeduction(index)}>-</button>)}
                                                {index === deductionsData.length - 1 && (<button type="button" className="btn btn-success btn-sm ms-1" onClick={handleAddDeduction}>+</button>)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {/* min={modalType === 'create' ? today : undefined} */}
                        {/* min={employeeSalaryData.start_date || today}  */}
                        <div className="row mt-3">
                            <div className="col-md-6"><div className="mb-3"><label className="form-label">Start Date <span className="text-danger"> *</span></label><input type="date" className="form-control" value={employeeSalaryData.start_date} min={selectedEmployeeJoiningDate || undefined} onChange={(e) => handleInputChange(e, 'start_date')} />{errors.start_date && <div className="text-danger" style={{ fontSize: '12px' }}>{errors.start_date}</div>}</div></div>
                            <div className="col-md-6"><div className="mb-3"><label className="form-label">End Date <span className="text-danger"> *</span></label><input type="date" className="form-control" value={employeeSalaryData.end_date} min={selectedEmployeeJoiningDate || undefined} onChange={(e) => handleInputChange(e, 'end_date')} />{errors.end_date && <div className="text-danger" style={{ fontSize: '12px' }}>{errors.end_date}</div>}</div></div>
                        </div>
                        <div className="row">
                            <div className="col-md-6"><div className="mb-3"><label className="form-label">Net Salary</label><input type="text" className="form-control text-end" value={`${employeeSalaryData.net_salary.toLocaleString()}`} readOnly />{errors.net_salary && <div className="text-danger" style={{ fontSize: '12px' }}>{errors.net_salary}</div>}</div></div>
                            <div className="col-md-6"><div className="mb-3 "><label className="form-label">Status</label><select className="form-select" value={employeeSalaryData.status} onChange={(e) => handleInputChange(e, 'status')}>{status.map((item: any) => (<option key={item.value} value={item.value}>{item.label}</option>))}</select></div></div>
                        </div>
                    </div>
                    <div className="modal-footer" style={{ position: 'sticky', bottom: 0, backgroundColor: 'white', zIndex: 1, padding: '1rem' }}> {/* Sticky footer */}
                        <button type="button" className="btn btn-white border me-2" onClick={handleCancel}>Cancel</button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            {loading && <span className="spinner-border spinner-border-sm me-2" />}
                            {modalType === "edit" ? "Update Structure" : "Save Structure"}
                        </button>
                    </div>
                </form>
            </Drawer >
            <Modal
                title="Sample CSV Download"
                centered
                width={700}
                open={isSampleModalVisible}
                onCancel={closeSampleModal}
                footer={[
                    <Button key="close" className="me-4" onClick={closeSampleModal}>Close</Button>,
                    <Button key="download" type="primary" onClick={handleDownloadSample} icon={<i className="ti ti-download me-1" />}>Download Sample</Button>
                ]}
            >
                <p>You can download the sample CSV file and use it as a template for uploading employee salary structures.</p>
                <div style={{ overflowX: "auto", maxHeight: 300, marginTop: 10 }}>
                    <table className="table table-bordered table-sm">
                        <thead>
                            <tr>
                                <th>employee_id</th>
                                <th>net_salary</th>
                                <th>start_date</th>
                                <th>end_date</th>
                                <th>overtime_pay_id</th>
                                <th>earning_name</th>
                                <th>earning_amount</th>
                                <th>earning_id</th>
                                <th>earning_percent</th>
                                <th>deduction_name</th>
                                <th>deduction_amount</th>
                                <th>deduction_id</th>
                                <th>deduction_percent</th>
                                <th>pay_scale_id</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>3</td><td>588.3</td><td>2025-06-01</td><td>2025-06-30</td><td>0.0</td>
                                <td>Basic</td><td>871.0</td><td>16.0</td><td>-</td>
                                <td>PAYEE</td><td>34.79</td><td>1</td><td>-</td><td>12</td>
                            </tr>
                            <tr>
                                <td>3</td><td>588.3</td><td>2025-06-01</td><td>2025-06-30</td><td>0.0</td>
                                <td>-</td><td>-</td><td>-</td><td>-</td>
                                <td>SNNIT</td><td>47.91</td><td>2</td><td>5.5</td><td>12</td>
                            </tr>
                            <tr>
                                <td>3</td><td>588.3</td><td>-</td><td>-</td><td>-</td>
                                <td>-</td><td>-</td><td>-</td><td>-</td>
                                <td>STAFF LOANS</td><td>200.00</td><td>7</td><td>-</td><td>12</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </Modal>
            <DeleteModal1 isVisible={isDeleteModalVisible} onClose={() => setIsDeleteModalVisible(false)} onConfirm={() => selectedRowData?.salary_structure_id && handleDeleteSalaryStructure(selectedRowData.salary_structure_id)} />
            <Drawer
                title={
                    <div className="d-flex align-items-center justify-content-between">
                        <span>
                            {`Salary Structure Logs`}
                            {employeeSalaryStructureLogs?.[0]?.employee_name
                                ? ` — ${employeeSalaryStructureLogs[0].employee_name}`
                                : selectedEmployeeId
                                    ? ` — #${selectedEmployeeId}`
                                    : ''}
                        </span>
                        <div>
                            {!!selectedEmployeeId && (
                                <Button
                                    size="small"
                                    onClick={() =>
                                        dispatch(fetchSalaryStructureLogsByEmployeeId({ employeeId: selectedEmployeeId! }))
                                    }
                                >
                                    Refresh
                                </Button>
                            )}
                        </div>
                    </div>
                }
                placement="right"
                width={520}
                open={isDrawerVisible}
                onClose={() => setIsDrawerVisible(false)}
                destroyOnClose
                style={{ marginTop: 50, paddingBottom: 50 }}
            >
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 180 }}>
                        <Spin />
                    </div>
                ) : (employeeSalaryStructureLogs?.length ?? 0) === 0 ? (
                    <Empty description="No logs found" />
                ) : (
                    <div style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                        {employeeSalaryStructureLogs.map((log: any) => (
                            <div key={log.log_id} className="border rounded p-2 mb-2">
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                    <Tag color={actionTagColor(log.action)}>{log.action}</Tag>
                                    <small className="text-muted">
                                        {safeDate(log.action_timestamp)}
                                    </small>
                                </div>

                                {/* Top line: employee + net salary */}
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <div className="fw-semibold">{log.employee_name || '-'}</div>
                                        <div className="text-muted small">Emp No: {log.employee_number || '-'}</div>
                                    </div>
                                    <div className="text-end">
                                        <div className="small text-muted">Net Salary</div>
                                        <div className="fw-semibold">{safeMoney(log.net_salary)}</div>
                                    </div>
                                </div>

                                <hr className="my-2" />

                                {/* Dates & status */}
                                <div className="row g-2">
                                    <div className="col-6">
                                        <div className="small text-muted">Start Date</div>
                                        <div>{safeDate(log.start_date)}</div>
                                    </div>
                                    <div className="col-6">
                                        <div className="small text-muted">End Date</div>
                                        <div>{safeDate(log.end_date)}</div>
                                    </div>
                                    <div className="col-6">
                                        <div className="small text-muted">Created At</div>
                                        <div>{safeDate(log.CreatedAt)}</div>
                                    </div>
                                    <div className="col-6">
                                        <div className="small text-muted">Modified At</div>
                                        <div>{safeDate(log.ModifiedAt)}</div>
                                    </div>
                                    <div className="col-6">
                                        <div className="small text-muted">Status</div>
                                        <div>{cap(log.status)}</div>
                                    </div>
                                    <div className="col-6">
                                        <div className="small text-muted">Pay Scale</div>
                                        <div>{log.SalaryRangeMin ?? '-'} - {log.SalaryRangeMax ?? '-'}</div>
                                    </div>
                                </div>
                                {/* Earnings & Deductions */}
                                {Array.isArray(log.earnings_json) && log.earnings_json.length > 0 && (
                                    <>
                                        <hr className="my-2" />
                                        <div>
                                            <div className="fw-semibold mb-1">Earnings</div>
                                            {log.earnings_json.map((earning: any) => (
                                                <div key={earning.id} className="d-flex justify-content-between small">
                                                    <div>
                                                        {earning.earning_name}
                                                        {earning.percent ? ` (${earning.percent}%)` : ""}
                                                    </div>
                                                    <div>{safeMoney(earning.amount)}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}

                                {Array.isArray(log.deductions_json) && log.deductions_json.length > 0 && (
                                    <>
                                        <hr className="my-2" />
                                        <div>
                                            <div className="fw-semibold mb-1">Deductions</div>
                                            {log.deductions_json.map((deduction: any) => (
                                                <div key={deduction.id} className="d-flex justify-content-between small">
                                                    <div>
                                                        {deduction.deduction_name}
                                                        {deduction.percent ? ` (${deduction.percent}%)` : ""}
                                                    </div>
                                                    <div>{safeMoney(deduction.amount)}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}

                                {/* Footer meta */}
                                <div className="mt-2 d-flex justify-content-between text-muted small">
                                    <div>Action By: {log.action_by_display_name ?? '-'}</div>
                                    {/* <div>Action By ID: {log.action_by_employee_number}</div> */}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Drawer>

        </>
    );
};

export default EmployeeSalaryStructure;